import React from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
// template components
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import Faq from './components/faq';
import GenericFafa from './components/genericFafa';
import HeroSection from './components/heroSection';
import Metadata from './components/metadata';
import ProductsAndServicesTabs from './components/productsAndServicesTabs';
import SuspectEmailCaptureForm from './components/suspectEmailCaptureForm';
import ValueProposition from './components/valueProposition';
// images
import highResImageHeroDesktop from './components/heroSection/images/home-hero-desktop.webp';
import highResImageHeroMobile from './components/heroSection/images/home-hero-mobile.webp';
// fonts
import {
  NmScript,
  NmSerif,
  SourceSans3Regular,
} from '../../assets/fonts';
// dislaimer footnotes
import {
  DividendsAreNotGuaranteed,
  HighestFinancialStrengthRatings,
  NerdWalletAllRightsReserved,
} from '../../utils/disclaimerFootnotes';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps } = useRouteData();

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      useReCaptcha
      dynamicConfig={dynamicConfig}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;Hal E. Hershfield,{' '}
          <Link
            variant='primaryDeemphasize'
            linkUrl='/assets/pdf/assessing-the-value-of-a-holistic-advisor.pdf'
            className='nmx-link'
            isInternalPdf>
            Assessing the Value of a Holistic Advisor (2024)
          </Link>
          . In Hal Hershfield&#39;s research, a comprehensive approach to planning means a client has Permanent Life Insurance, either investments or annuities, and a recent financial plan.
        </>,
        <>
          {HighestFinancialStrengthRatings(2)}
        </>,
        <>
          <sup>3</sup>&nbsp;Ranking for Northwestern Mutual Investment Services LLC. Measured by 2022 Revenue. Source: InvestmentNews, May 2023.
        </>,
        <>
          {DividendsAreNotGuaranteed(4)}
        </>,
        <>
          {NerdWalletAllRightsReserved(5)}
        </>,
      ]}
      // For now, use className='nmx-link' for any CustomDisclaimer Link Components
      customDisclaimers={[
        <>
          To learn more about Northwestern Mutual Investment Services, LLC and its financial professionals, visit{' '}
          <Link
            linkUrl='https://brokercheck.finra.org/'
            isOutgoingLink
            className='nmx-link'
            variant='primaryDeemphasize'>
            FINRA BrokerCheck&#174;
          </Link>. Visit our <Link
            linkUrl='/client-relationship-summary/'
            isOutgoingLink
            className='nmx-link'
            variant='primaryDeemphasize'>
            Client Relationship Summaries
          </Link> (Form CRS) which provide brief information about our firms that provide brokerage services and advisory services.
        </>,
      ]}
      disableDisclaimerTopBorder
      useAkzidenz>

      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResImageHeroMobile} as='image' media={`(max-width: ${Theme.breakpoints.values.small}px)`} />
        <link rel='preload' href={highResImageHeroDesktop} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
        <link rel='stylesheet' href='https://use.typekit.net/typ5dev.css' />
      </Head>

      {/* loading fonts */}
      <NmScript />
      <NmSerif />
      <SourceSans3Regular />

      <Metadata
        image={highResImageHeroDesktop}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
        imageAltText='Two happy people sitting together and chatting over coffee.'
      />

      <HeroSection />

      <ProductsAndServicesTabs />

      <AdvisorConnectModule />

      <ValueProposition />

      <SuspectEmailCaptureForm
        sectionId='home-page-suspect-email-capture-form'
        formLocation='home'
        config={config}
        journey='selfSubscribeLifeAndMoneyNewsletter'
        position='static' // optional prop for analytics
        headingText={<>Get financial tips, tools, and more with our monthly Life &#38; Money newsletter.</>}
      />

      <CredentialCalloutGrouping />

      <Faq />

      <GenericFafa />

    </Page>
  );
};
